import { Entity, registerComponent, registerSystem } from "aframe";
import { Vector3 } from "three";
import { OrientationSystem } from "../orientation";
import { GetRayCallback, InputActionDetail, InputComponent, InputSystem, InputSystemData, inputSystemSchema, press, release } from "./common";



// KeyMap that replaces user-defined keys when entering UI mode :
const uiKeyMap : InputSystemData = {
	"0" : "Action", //Left click : activate
	"2" : "Menu", //Right click : close menu
	"WheelUp" : "ScrollUp", //Wheel : scroll
	"WheelDown" : "ScrollDown"
};



export interface MouseInputSystem extends InputSystem
{
	mouseClientPosition? : { x : number, y : number };
	mousePosition? : { x : number, y : number };
	pointer? : Entity;
	getRay(origin : any, direction : any, setter : Function, position? : { x : number, y : number }) : void;
}


registerSystem("mouse", ( () => {
	const rayDir = new Vector3();
	return {
		schema: inputSystemSchema,
		init(this : MouseInputSystem)
		{
			const oriSystem = this.el.systems.orientation as OrientationSystem;

			const updateMousePosition = (pos : { clientX : number, clientY : number } ) =>
			{
				const noneBefore = !this.mousePosition;

				this.mouseClientPosition = oriSystem.convertCoordinates(pos);
				this.mousePosition = {
					x : (2.0 * (this.mouseClientPosition.x / this.el.clientWidth) - 1.0),
					y : (1.0 - 2.0 * (this.mouseClientPosition.y / this.el.clientHeight))
				};

				delete this.pointer; //Ignore 3D pointer and get back to real mouse pointer.

				if(noneBefore)
					this.el.emit("enter-mouse", this.mousePosition);
			};


			const pointerLeave = () =>
			{
				if(!this.mousePosition) return;
				delete this.mouseClientPosition;
				delete this.mousePosition;
				this.el.emit("exit-mouse");
			};
			
			const activePointers : { [id: string] : PointerEvent } = {};

			const pointerEnd = ( id : number|string) =>
			{
				if(!(id in activePointers)) return;
				
				delete activePointers[id];
				for(let anyPointer in activePointers) return;

				//Finish current frame with last known position, then clear the pointer
				requestAnimationFrame( () => {
					for(let anyPointer in activePointers) return;
					pointerLeave();
				});
			};

			const pointermove = (event : PointerEvent) => updateMousePosition(event);
			const pointerdown = (event : PointerEvent) =>
			{
				activePointers[ event.pointerId ] = event;
				updateMousePosition(event);
			};
			const pointerup = (event : PointerEvent) =>
			{
				updateMousePosition(event);
				pointerEnd(event.pointerId);
			};	

			window.addEventListener("pointerdown", pointerdown, { capture : true } );
			window.addEventListener("pointermove", pointermove, { capture : true });
			window.addEventListener("pointerup", pointerup, { capture : true });

			document.documentElement.addEventListener("pointerenter", (event : PointerEvent) =>
			{
				if(event.eventPhase != Event.AT_TARGET) return;
				activePointers[ event.pointerType ] = event;
			}, { capture : true } );

			document.documentElement.addEventListener("pointerleave", (event : PointerEvent) =>
			{
				if(event.eventPhase != Event.AT_TARGET) return;
				pointerEnd(event.pointerType);
			}, { capture : true } );

			this.el.addEventListener("disable-mouse", pointerLeave);
			this.el.addEventListener("set-mouse-pointer", (event) => { this.pointer = event.target as Entity; pointerLeave(); } );
			this.el.addEventListener("reset-mouse-pointer", (event) => { if(this.pointer === event.target) delete this.pointer; } );
		},

		
		getRay(this : MouseInputSystem, origin : any, direction : any, setter : Function, position? : { x:number, y:number }) : void
		{
			const cam = this.el.camera, camMat = cam.matrixWorld.elements;

			setter.call(origin, camMat[12], camMat[13], camMat[14] );
			if(this.pointer)
			{
				this.pointer.object3D.updateWorldMatrix(true, false);
				const ptrMat = this.pointer.object3D.matrixWorld.elements;
				setter.call(direction, ptrMat[12] - camMat[12], ptrMat[13] - camMat[13], ptrMat[14] - camMat[14] );
			}
			else
			{
				const pos = position || this.mousePosition || { x:0, y:0 };
				rayDir.set( pos.x, pos.y, 1.0).unproject(cam);
				setter.call(direction, rayDir.x, rayDir.y, rayDir.z);
			}
		} 
	};
 } )() 
);



registerComponent("mouse", {


	init(this : InputComponent & { system : MouseInputSystem })
	{
		const scene = this.system.el;
		
		const keyMap = (key : string) => (scene.is("ui-mode") ? uiKeyMap : this.system.data)[key];

		this.actions = {};

		const getRay = (origin : any, direction : any, setter : Function) => this.system.getRay(origin, direction, setter, this.system.mousePosition );
		const feedback = (i : number) => this.feedback(i);
		const instantRay = ( ) =>
		{
			const org = new Vector3(), dir = new Vector3();
			this.system.getRay(org, dir, Vector3.prototype.set, this.system.mousePosition! );
			const callback : GetRayCallback = (o, d, set) => { set.call(o, org.x, org.y, org.z); set.call(d, dir.x, dir.y, dir.z); };
			return callback;
		};
		
		const instantEvent = (key : string, amount : number) =>
		{
			const action = keyMap(key);
			if(!action) return;
			
			const detail : InputActionDetail = 
			{
				input : this, timestamp : Date.now(), action,	feedback, amount, getRay : instantRay(), instant : true
			};
			this.el.emit("input-begin", detail);
			return detail;
		}

		const kill = (e : Event) => {
			e.preventDefault();
			e.stopPropagation();
		}
		
		const mousedown = (e : PointerEvent) =>
		{
			if(e.eventPhase != Event.AT_TARGET) return; //Other element.
			scene.canvas.setPointerCapture(e.pointerId);

			//if(e.target !== scene.canvas) return;
			const key = e.button.toString();
			const action = keyMap(key);
			const amount = (e.pointerType == "mouse") ? 1.0 : e.pressure;
			return action && press(this, action, key, amount, getRay) && kill(e);
		};
		const mouseup = (e : PointerEvent) =>
		{
			if( !e.buttons )
				scene.canvas.releasePointerCapture(e.pointerId);

			const key = e.button.toString();
			const action = keyMap(key);
			return action && release(this, action, key) && kill(e);
		};
		const dblclick = (e : MouseEvent) =>
		{
			if(e.eventPhase != Event.AT_TARGET) return; //Other element.
			instantEvent("Double" + e.button.toString(), 1);
			kill(e);
		}
		const wheel = (e : WheelEvent) =>
		{
			if(e.eventPhase != Event.AT_TARGET) return; //Other element.
			instantEvent((e.deltaY > 0) ? "WheelDown" : "WheelUp", 1) && kill(e);
		}

		scene.canvas.addEventListener("pointerdown", mousedown as EventListener);
		window.addEventListener("pointerup", mouseup);
		
		scene.canvas.addEventListener("dblclick", dblclick as EventListener);
		scene.canvas.addEventListener("wheel", wheel as EventListener);

		//When changing between ui/game because of a mousedown event, mouseup doesn't end the action :
		const endActions = (map : InputSystemData) =>
		{
			for(let key in map)
			{
				const action = map[key];
				if(action in this.actions)
					release(this, action, key );
			}
		};
		scene.addEventListener("enter-ui", () => endActions(this.system.data));
		scene.addEventListener("exit-ui", () => endActions(uiKeyMap));
	},
	feedback(this : InputComponent, intensity : number) {}

})
